import { z } from 'zod';
import { defineProxyApi, JsonValueSchema, ProxyRouteQuerySchema, } from '../base.js';
/*
  These environment variables are used to create the Axios client
  used by the chat-api to proxy requests.
*/
export const MslMcpProxyConfigSchema = z.object({
    CUSTOM_PAGE_ROUTE_MSL_MCP_API_URL: z
        .url()
        .optional()
        .describe('Base URL for the msl-mcp custom-page proxy target.'),
    CUSTOM_PAGE_ROUTE_MSL_MCP_SCOPES: z
        .string()
        .prefault('read')
        .describe('Delegation scope used for msl-mcp custom-page proxy self-signed JWT.'),
    CUSTOM_PAGE_ROUTE_MSL_MCP_AUDIENCE: z
        .string()
        .optional()
        .describe('Audience used for msl-mcp custom-page proxy self-signed JWT.'),
});
/*
  Define all proxy routes here. The chat-api will use these definitions to validate requests
  and to construct the proxied API client used by components in `custom-pages-registry`.
*/
export const mslMcpProxyApi = defineProxyApi({
    clientKey: 'msl-mcp',
    routes: {
        'hcp-profile': {
            method: 'POST',
            path: '/hcp/profile',
            timeoutMs: 15000,
            requestSchema: z.object({
                context: z.object({
                    brand: z.object({ identifier: z.string() }),
                    subject: z.object({ type: z.literal('hcp'), id: z.union([z.string(), z.number()]) }),
                    user: z.object({ email: z.string() }),
                }),
            }),
            responseSchema: JsonValueSchema,
        },
        'hcp-search': {
            method: 'POST',
            path: '/hcp/search',
            timeoutMs: 20000,
            requestSchema: z.object({
                brand_identifier: z.string(),
                name: z.string().optional(),
                country: z.string().optional(),
                specialty: z.string().optional(),
                territory_scope: z.enum(['all', 'user']).optional(),
                requesting_user_email: z.string().optional(),
                territory_insight_only: z.boolean().optional(),
                sort_by: z.enum(['relevance', 'name', 'tier', 'providerType']).optional(),
                sort_dir: z.enum(['asc', 'desc']).optional(),
                size: z.coerce.number().int().min(1).max(100).optional(),
            }),
            responseSchema: JsonValueSchema,
        },
        'territory-hcps': {
            method: 'POST',
            path: '/hcp/search',
            timeoutMs: 20000,
            requestSchema: z.object({
                brand_identifier: z.string(),
                territory_scope: z.enum(['all', 'user']).default('user'),
                requesting_user_email: z.string().optional(),
                territory_insight_only: z.boolean().default(false),
                sort_by: z.literal('name').optional(),
                sort_dir: z.literal('asc').optional(),
                size: z.coerce.number().int().min(1).max(100).optional(),
            }),
            responseSchema: JsonValueSchema,
        },
    },
    configSchema: MslMcpProxyConfigSchema,
    buildHttpClientConfig: (config) => {
        if (!config.CUSTOM_PAGE_ROUTE_MSL_MCP_API_URL) {
            throw new Error('Missing required proxy config: CUSTOM_PAGE_ROUTE_MSL_MCP_API_URL');
        }
        if (!config.CUSTOM_PAGE_ROUTE_MSL_MCP_AUDIENCE) {
            throw new Error('Missing required proxy config: CUSTOM_PAGE_ROUTE_MSL_MCP_AUDIENCE');
        }
        return {
            apiUrl: config.CUSTOM_PAGE_ROUTE_MSL_MCP_API_URL,
            apiPath: '',
            auth: {
                type: 'internal-delegation-jwt',
                audience: config.CUSTOM_PAGE_ROUTE_MSL_MCP_AUDIENCE,
                scope: config.CUSTOM_PAGE_ROUTE_MSL_MCP_SCOPES,
            },
        };
    },
});
export const MSL_MCP_PROXY_CLIENT_KEY = mslMcpProxyApi.clientKey;
export const MslMcpRouteLabelSchema = mslMcpProxyApi.routeLabelSchema;
export const MslMcpProxyQuerySchema = ProxyRouteQuerySchema;
export const MslMcpProxyRequestSchema = mslMcpProxyApi.proxyRequestSchema;
export function getMslMcpRouteDefinition(routeLabel) {
    return mslMcpProxyApi.getRouteDefinition(routeLabel);
}
